import { useAuthRequest, useLogoutRequest } from 'mmfintech-checkout-commons'
import { tr } from 'mmfintech-commons'
import { useEffect, useState } from 'react'
import { TailSpin } from 'react-loader-spinner'
import { shallowEqual, useSelector } from 'react-redux'
import { BackToMerchantButton, CoreButton, MerchantPane } from '../../components'
import { WalletCheckoutAccount } from './WalletCheckoutAccount'
import { WalletCheckoutLogin } from './WalletCheckoutLogin'
import { WalletCheckoutOtp } from './WalletCheckoutOtp'
import { WalletCheckoutPreview } from './WalletCheckoutPreview'

interface WalletCheckoutProps {
  sessionId: string
  handlePay: (accountId: number) => void
  onCancel: () => void
  hasStorageAccess: boolean
  setStorageAccess: (enable: boolean) => void
}

enum CheckoutSteps {
  LOADING = 'LOADING',
  OPEN_POPUP = 'OPEN_POPUP',
  REQUEST_ACCESS = 'REQUEST_ACCESS',
  LOGIN = 'LOGIN',
  OTP = 'OTP',
  SELECT_ACCOUNT = 'SELECT_ACCOUNT',
  PREVIEW = 'PREVIEW'
}

export const WalletCheckout = (props: WalletCheckoutProps) => {
  const { sessionId, handlePay, onCancel, hasStorageAccess, setStorageAccess } = props

  const { logo, session } = useSelector(
    ({ checkout: { logo, session } }) => ({
      logo,
      session
    }),
    shallowEqual
  )

  const [step, setStep] = useState<CheckoutSteps>(CheckoutSteps.LOADING)
  const [selectedAccount, setSelectedAccount] = useState(null)

  const { authRequest } = useAuthRequest()
  const { logoutRequest } = useLogoutRequest()

  const handleCancel = () => {
    logoutRequest()
    onCancel()
  }

  const handleLoginResponse = (response: any) => {
    setStep(response?.data?.challenge ? CheckoutSteps.OTP : CheckoutSteps.SELECT_ACCOUNT)
  }

  const requestAccess = () => {
    const noAccess = () => {
      console.error('Cannot obtain storage access')
      onCancel()
    }

    const continueWithPayment = () => {
      setStorageAccess(true)
      authRequest(
        () => setStep(CheckoutSteps.SELECT_ACCOUNT),
        () => setStep(CheckoutSteps.LOGIN)
      )
    }

    if (typeof document.requestStorageAccess === 'function') {
      document
        .requestStorageAccess()
        .then(
          () => continueWithPayment(),
          () => noAccess()
        )
        .catch(() => {
          noAccess()
        })
    } else {
      continueWithPayment()
    }
  }

  const openPopup = () => {
    setStep(CheckoutSteps.LOADING)
    const handler = window.open(
      `${window.origin}/auth/sessionId/${sessionId}`,
      '_blank',
      'popup=1,location=0,toolbar=0,menubar=0,status=0'
    )
    if (handler) {
      const interval = setInterval(() => {
        if (handler.closed) {
          clearInterval(interval)
          //@ts-ignore
          if (handler.userLoggedIn) {
            setStep(CheckoutSteps.REQUEST_ACCESS)
          } else {
            onCancel()
          }
        }
      }, 500)
    } else {
      onCancel()
    }
  }

  useEffect(() => {
    if (hasStorageAccess) {
      setStep(!session?.isSubsequent ? CheckoutSteps.LOGIN : CheckoutSteps.SELECT_ACCOUNT)
    } else {
      setStep(CheckoutSteps.OPEN_POPUP)
    }

    return () => {
      setStep(CheckoutSteps.LOADING)
    }
  }, [])

  return (
    <>
      {step !== CheckoutSteps.LOADING && <MerchantPane session={session} logo={logo} />}

      {step === CheckoutSteps.LOADING && (
        <div className='mb-4 mt-4'>
          <TailSpin width='8rem' height='8rem' color='white' />
        </div>
      )}

      {step === CheckoutSteps.OPEN_POPUP && (
        <div className='payment-wrapper' data-test='wallet-checkout-request-access'>
          <div className='inner-subtitle'>{tr('CHECKOUT.WALLET_PAYMENT.OPEN_POPUP_TITLE', 'Sign in')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.OPEN_POPUP',
              'In order to continue with The Kingdom Bank, please click the button below. A new popup window will appear.'
            )}
          </p>

          <div className='buttons mt-4'>
            <CoreButton
              type='button'
              variation='primary'
              className='mb-1'
              text={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={openPopup}
            />
            <CoreButton
              type='button'
              variation='tertiary'
              text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
              onClick={onCancel}
              data-test='button-back'
            />
          </div>
        </div>
      )}

      {step === CheckoutSteps.REQUEST_ACCESS && (
        <div className='payment-wrapper' data-test='wallet-checkout-request-access'>
          <div className='inner-subtitle'>{tr('CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST_TITLE', 'Cookie consent')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST',
              'Our website use cookies to provide safe user experience. Please click the button below to accept.'
            )}
          </p>

          <div className='mt-4 buttons'>
            <CoreButton
              type='button'
              variation='primary'
              className='mb-1'
              fullWidth
              text={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={requestAccess}
            />
            <CoreButton
              type='button'
              variation='tertiary'
              text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
              onClick={onCancel}
              data-test='button-back'
            />
          </div>
        </div>
      )}

      {step === CheckoutSteps.LOGIN && (
        <div className='payment-wrapper'>
          <WalletCheckoutLogin
            sessionId={sessionId}
            session={session}
            onSuccess={handleLoginResponse}
            onCancel={onCancel}
          />
        </div>
      )}

      {step === CheckoutSteps.OTP && (
        <WalletCheckoutOtp onSuccess={() => setStep(CheckoutSteps.SELECT_ACCOUNT)} onCancel={onCancel} />
      )}

      {step === CheckoutSteps.SELECT_ACCOUNT && (
        <div className='payment-wrapper'>
          <WalletCheckoutAccount
            onSuccess={() => setStep(CheckoutSteps.PREVIEW)}
            onCancel={handleCancel}
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
          />
        </div>
      )}

      {step === CheckoutSteps.PREVIEW && (
        <WalletCheckoutPreview
          onCancel={() => setStep(CheckoutSteps.SELECT_ACCOUNT)}
          selectedAccount={selectedAccount}
          handlePay={() => handlePay(selectedAccount.id)}
        />
      )}

      <BackToMerchantButton className='mt-2' returnUrl={session?.returnUrl} />
    </>
  )
}
